import { useCallback, useEffect, useMemo, useState } from 'react';
import { PCMP_QUEUE_PAGE_SIZE, PCMP_REASON_STATUS } from '../constants/pcmpQueue.constants';
import type {
  PcmpDisbursementConfirmStatus,
  PcmpDisbursementVerifyStatus,
} from '../types/pcmpProposal.types';
import type { PcmpDivision, PcmpQueueFilters, PcmpQueueSearch } from '../types/pcmpQueue.types';

interface PcmpQueueDraftFilters {
  projectId: number | undefined;
  departmentId: number | undefined;
  division: PcmpDivision | undefined;
  refNo: string;
  teacher: string;
  dateFrom: string;
  dateTo: string;
  status: string | undefined;
  reason: string | undefined;
  batchNo: string;
  accDisbConfirm: PcmpDisbursementConfirmStatus | undefined;
  disbVerify: PcmpDisbursementVerifyStatus | undefined;
}

const INITIAL_FILTERS: PcmpQueueDraftFilters = {
  projectId: undefined,
  departmentId: undefined,
  division: undefined,
  refNo: '',
  teacher: '',
  dateFrom: '',
  dateTo: '',
  status: undefined,
  reason: undefined,
  batchNo: '',
  accDisbConfirm: undefined,
  disbVerify: undefined,
};

// Every filter field of the URL cleared, so an apply or reset replaces them all.
const CLEARED_SEARCH_FILTERS = {
  projectId: undefined,
  departmentId: undefined,
  division: undefined,
  refNo: undefined,
  teacher: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  resolvedStatus: undefined,
  reason: undefined,
  batchNo: undefined,
  accDisbConfirm: undefined,
  disbVerify: undefined,
} as const satisfies Omit<PcmpQueueSearch, 'page' | 'limit'>;

const BATCH_NO_RE = /^\d+$/;

export function hasPcmpReasonFilter(status: string | undefined): boolean {
  return status !== undefined && status in PCMP_REASON_STATUS;
}

/** The URL search fields of applied filters, e.g. to carry them to another page. */
export function toPcmpQueueSearch({ status, ...rest }: PcmpQueueFilters) {
  return { ...rest, resolvedStatus: status } satisfies Omit<PcmpQueueSearch, 'page' | 'limit'>;
}

/**
 * The filters, paging and draft-then-apply flow every PCMP queue shares. The
 * queue's route hook owns the URL and its own params (tab and the like).
 */
export function usePcmpQueueFilters<S extends PcmpQueueSearch>(
  search: S,
  setSearch: (update: (prev: S) => S) => void,
) {
  const page = search.page ?? 1;
  const limit = search.limit ?? PCMP_QUEUE_PAGE_SIZE;

  const appliedFilters = useMemo<PcmpQueueFilters>(
    () => ({
      projectId: search.projectId,
      departmentId: search.departmentId,
      division: search.division,
      refNo: search.refNo,
      teacher: search.teacher,
      dateFrom: search.dateFrom,
      dateTo: search.dateTo,
      status: search.resolvedStatus,
      reason: hasPcmpReasonFilter(search.resolvedStatus) ? search.reason : undefined,
      batchNo: search.batchNo,
      accDisbConfirm: search.accDisbConfirm,
      disbVerify: search.disbVerify,
    }),
    [
      search.projectId,
      search.departmentId,
      search.division,
      search.refNo,
      search.teacher,
      search.dateFrom,
      search.dateTo,
      search.resolvedStatus,
      search.reason,
      search.batchNo,
      search.accDisbConfirm,
      search.disbVerify,
    ],
  );

  const appliedDraft = useMemo<PcmpQueueDraftFilters>(
    () => ({
      projectId: appliedFilters.projectId,
      departmentId: appliedFilters.departmentId,
      division: appliedFilters.division,
      refNo: appliedFilters.refNo ?? '',
      teacher: appliedFilters.teacher ?? '',
      dateFrom: appliedFilters.dateFrom ?? '',
      dateTo: appliedFilters.dateTo ?? '',
      status: appliedFilters.status,
      reason: appliedFilters.reason,
      batchNo: appliedFilters.batchNo === undefined ? '' : String(appliedFilters.batchNo),
      accDisbConfirm: appliedFilters.accDisbConfirm,
      disbVerify: appliedFilters.disbVerify,
    }),
    [appliedFilters],
  );

  const [draftFilters, setDraftFilters] = useState<PcmpQueueDraftFilters>(appliedDraft);

  useEffect(() => {
    setDraftFilters(appliedDraft);
  }, [appliedDraft]);

  const activeFilterCount = Object.values(appliedFilters).filter(
    (value) => value !== undefined,
  ).length;

  const isDateRangeInvalid =
    draftFilters.dateFrom !== '' &&
    draftFilters.dateTo !== '' &&
    draftFilters.dateFrom > draftFilters.dateTo;

  const trimmedBatchNo = draftFilters.batchNo.trim();
  const isBatchNoInvalid =
    trimmedBatchNo !== '' && (!BATCH_NO_RE.test(trimmedBatchNo) || Number(trimmedBatchNo) < 1);

  const setDraft = useCallback(
    <K extends keyof PcmpQueueDraftFilters>(key: K, value: PcmpQueueDraftFilters[K]) => {
      setDraftFilters((prev) => {
        const next = { ...prev, [key]: value };
        // A reason only narrows the status it belongs to.
        if (key === 'status' && !hasPcmpReasonFilter(next.status)) next.reason = undefined;
        return next;
      });
    },
    [],
  );

  const handleApply = useCallback(() => {
    if (isDateRangeInvalid || isBatchNoInvalid) return;
    setSearch((prev) => ({
      ...prev,
      page: undefined,
      projectId: draftFilters.projectId,
      departmentId: draftFilters.departmentId,
      division: draftFilters.division,
      refNo: draftFilters.refNo.trim() || undefined,
      teacher: draftFilters.teacher.trim() || undefined,
      dateFrom: draftFilters.dateFrom || undefined,
      dateTo: draftFilters.dateTo || undefined,
      resolvedStatus: draftFilters.status,
      reason: draftFilters.reason,
      batchNo: trimmedBatchNo ? Number(trimmedBatchNo) : undefined,
      accDisbConfirm: draftFilters.accDisbConfirm,
      disbVerify: draftFilters.disbVerify,
    }));
  }, [draftFilters, trimmedBatchNo, isDateRangeInvalid, isBatchNoInvalid, setSearch]);

  const handleReset = useCallback(() => {
    setDraftFilters(INITIAL_FILTERS);
    setSearch((prev) => ({ ...prev, ...CLEARED_SEARCH_FILTERS, page: undefined }));
  }, [setSearch]);

  const setPage = useCallback(
    (next: number) => {
      setSearch((prev) => ({ ...prev, page: next > 1 ? next : undefined }));
    },
    [setSearch],
  );

  const setLimit = useCallback(
    (next: number) => {
      setSearch((prev) => ({
        ...prev,
        limit: next === PCMP_QUEUE_PAGE_SIZE ? undefined : next,
        page: undefined,
      }));
    },
    [setSearch],
  );

  return {
    page,
    limit,
    filters: draftFilters,
    appliedFilters,
    activeFilterCount,
    isDateRangeInvalid,
    isBatchNoInvalid,
    setDraft,
    handleApply,
    handleReset,
    setPage,
    setLimit,
  };
}

export type PcmpQueueFilterController = ReturnType<typeof usePcmpQueueFilters>;
